import { useEffect, useRef, useState } from "react";
import { PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { ENC } from "@/constants/testIds";
import {
  CLICK_DRAG_THRESHOLD_PX,
  DIVIDER_PX,
  chartLayoutMode,
  chartPaneFlex,
  clickChartDivider,
  codingPaneFlex,
  dragChart,
  isClickNotDrag,
  percentFromDelta,
  toggleCodingPanel,
} from "@/lib/encounterWorkspaceLayout";

function ChartDivider({ state, getWidth, onStateChange }) {
  const startRef = useRef(null);

  const onPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    startRef.current = { x: event.clientX, moved: false, state, width: getWidth() };
  };

  const onPointerMove = (event) => {
    const start = startRef.current;
    if (!start?.state) return;
    if (!isClickNotDrag(start.x, event.clientX, CLICK_DRAG_THRESHOLD_PX)) {
      start.moved = true;
    }
    if (!start.moved) return;
    const delta = percentFromDelta(event.clientX - start.x, start.width);
    onStateChange((current) => dragChart(current, start.state.chartPercent + delta));
  };

  const onPointerUp = () => {
    const start = startRef.current;
    startRef.current = null;
    if (!start || start.moved) return;
    onStateChange((current) => clickChartDivider(current));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      data-testid={ENC.screenChartDivider}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="emr-encdetail__divider group"
      style={{ width: DIVIDER_PX }}
      title="Drag to resize, click to collapse the Patient Chart"
    >
      <span className="emr-encdetail__divider-line" />
      <span className="emr-encdetail__divider-grip" />
    </div>
  );
}

export default function EncounterWorkspacePanes({
  state,
  onStateChange,
  chart,
  documentation,
  coding,
}) {
  const groupRef = useRef(null);
  const [mode, setMode] = useState(() =>
    typeof window === "undefined" ? "columns" : chartLayoutMode(window.innerWidth),
  );

  useEffect(() => {
    const sync = () => setMode(chartLayoutMode(window.innerWidth));
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const getWidth = () => groupRef.current?.getBoundingClientRect().width || 0;
  const changeState = (updater) => onStateChange((current) => updater(current));
  const toggleChart = () => changeState((current) => clickChartDivider(current));
  const toggleCoding = () => changeState((current) => toggleCodingPanel(current));

  if (mode === "stacked") {
    return (
      <div
        ref={groupRef}
        data-testid={ENC.screenWorkspace}
        className="flex min-h-0 flex-1 flex-col gap-2"
      >
        <section
          data-testid={ENC.screenPatientColumn}
          className="emr-encdetail__pane overflow-hidden"
        >
          <button
            type="button"
            data-testid={state.chartCollapsed ? ENC.screenChartExpand : ENC.screenChartCollapse}
            onClick={toggleChart}
            className="emr-encdetail__pane-toggle"
          >
            <span className="emr-encdetail__pane-title">Patient Chart</span>
            <span className="emr-encdetail__pane-toggle-meta">
              {state.chartCollapsed ? "Show" : "Hide"}
            </span>
          </button>
          {!state.chartCollapsed ? (
            <div className="emr-encdetail__chart-body max-h-[60vh] overflow-y-auto border-t border-slate-100">
              {chart}
            </div>
          ) : null}
        </section>

        <section
          data-testid={ENC.screenPaneNotes}
          className="emr-encdetail__pane flex min-h-[24rem] flex-1 flex-col overflow-hidden"
        >
          {documentation}
        </section>

        <section
          data-testid={ENC.screenCodingColumn}
          className="emr-encdetail__pane overflow-hidden"
        >
          <button
            type="button"
            data-testid={state.codingCollapsed ? ENC.screenCodingExpand : ENC.screenCodingCollapse}
            onClick={toggleCoding}
            className="emr-encdetail__pane-toggle"
          >
            <span className="emr-encdetail__pane-title">Diagnosis · CPT · Orders</span>
            <span className="emr-encdetail__pane-toggle-meta">
              {state.codingCollapsed ? "Show" : "Hide"}
            </span>
          </button>
          {!state.codingCollapsed ? (
            <div className="emr-encdetail__chart-body max-h-[60vh] overflow-y-auto border-t border-slate-100">
              {coding}
            </div>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div
      ref={groupRef}
      data-testid={ENC.screenWorkspace}
      className="flex min-h-0 min-w-0 flex-1"
    >
      <section
        data-testid={ENC.screenPatientColumn}
        className="emr-encdetail__pane flex min-h-0 flex-col overflow-hidden"
        style={{ flex: chartPaneFlex(state) }}
      >
        {state.chartCollapsed ? (
          <button
            type="button"
            data-testid={ENC.screenChartExpand}
            onClick={toggleChart}
            className="emr-encdetail__restore"
            aria-label="Expand Patient Chart"
            title="Expand Patient Chart"
          >
            <PanelLeftOpen className="h-4 w-4" />
            <span className="emr-encdetail__restore-label">Patient Chart</span>
          </button>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">{chart}</div>
        )}
      </section>

      {!state.chartCollapsed ? (
        <ChartDivider state={state} getWidth={getWidth} onStateChange={changeState} />
      ) : null}

      <section
        data-testid={ENC.screenPaneNotes}
        className="emr-encdetail__pane flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        {documentation}
      </section>

      <section
        data-testid={ENC.screenCodingColumn}
        className="emr-encdetail__pane flex min-h-0 flex-col overflow-hidden"
        style={{ flex: codingPaneFlex(state) }}
      >
        {state.codingCollapsed ? (
          <button
            type="button"
            data-testid={ENC.screenCodingExpand}
            onClick={toggleCoding}
            className="emr-encdetail__restore"
            aria-label="Expand Diagnosis, CPT, and Orders"
            title="Expand Diagnosis, CPT, and Orders"
          >
            <PanelRightOpen className="h-4 w-4" />
            <span className="emr-encdetail__restore-label">Diagnosis · CPT · Orders</span>
          </button>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">{coding}</div>
        )}
      </section>
    </div>
  );
}
